import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, Plus, Users, TrendingUp, Calendar, Edit, Trash2, 
  FileText, DollarSign, BarChart3, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LabourTracking = () => {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [logForm, setLogForm] = useState({
    project_id: '',
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    work_type: 'regular',
    billable: true
  });

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, logsRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/labour/summary`, { params: dateRange }),
        axios.get(`${API}/labour/logs`, { params: dateRange }),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`)
      ]);
      
      setSummary(summaryRes.data);
      setLogs(logsRes.data.logs || []);
      setProjects(projectsRes.data.projects || []);
      setEmployees(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load labour data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLogModal = (log = null) => {
    if (log) {
      setEditingLog(log);
      setLogForm({
        project_id: log.project_id || '',
        hours: log.hours || '',
        description: log.description || '',
        date: log.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        work_type: log.work_type || 'regular',
        billable: log.billable !== false
      });
    } else {
      setEditingLog(null);
      setLogForm({
        project_id: '',
        hours: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        work_type: 'regular',
        billable: true
      });
    }
    setShowLogModal(true);
  };

  const handleSaveLog = async () => {
    if (!logForm.project_id || !logForm.hours) {
      toast.error('Please fill in project and hours');
      return;
    }

    try {
      if (editingLog) {
        await axios.put(`${API}/labour/log/${editingLog.id}`, logForm);
        toast.success('Time log updated');
      } else {
        await axios.post(`${API}/labour/log`, logForm);
        toast.success('Time logged successfully');
      }
      setShowLogModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save time log');
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this time log?')) return;
    
    try {
      await axios.delete(`${API}/labour/log/${logId}`);
      toast.success('Time log deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete time log');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="labour-tracking-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Labour Tracking
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Time logging, analytics, and labour cost management
          </p>
        </div>
        <Button onClick={() => handleOpenLogModal()} data-testid="log-time-btn">
          <Plus className="h-4 w-4 mr-2" />
          Log Time
        </Button>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
            <Button variant="outline" onClick={loadData}>
              Apply Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.total_hours}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.total_entries}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Time Entries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.by_employee?.length || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Team Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{summary.by_project?.length || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="logs" className="w-full">
        <TabsList>
          <TabsTrigger value="logs">Time Logs</TabsTrigger>
          <TabsTrigger value="by-employee">By Employee</TabsTrigger>
          <TabsTrigger value="by-project">By Project</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">Recent Time Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No time logs found</p>
                  <Button className="mt-4" onClick={() => handleOpenLogModal()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Log Your First Entry
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 text-center">
                          <p className="text-lg font-bold text-foreground">{log.hours}h</p>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{log.project_name || 'Unknown Project'}</p>
                          <p className="text-sm text-muted-foreground">
                            {log.employee_name} • {formatDate(log.date)}
                          </p>
                          {log.description && (
                            <p className="text-xs text-muted-foreground mt-1">{log.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={log.work_type === 'overtime' ? 'destructive' : 'secondary'}>
                          {log.work_type}
                        </Badge>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenLogModal(log)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteLog(log.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-employee" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">Hours by Employee</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.by_employee?.length > 0 ? (
                <div className="space-y-3">
                  {summary.by_employee.map((emp, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="font-bold text-primary">{emp.name?.charAt(0) || '?'}</span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{emp.name}</p>
                          <p className="text-sm text-muted-foreground">{emp.entries} entries</p>
                        </div>
                      </div>
                      <p className="text-xl font-bold text-foreground">{emp.hours.toFixed(1)}h</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No employee data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-project" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">Hours by Project</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.by_project?.length > 0 ? (
                <div className="space-y-3">
                  {summary.by_project.map((proj, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium text-foreground">{proj.name}</p>
                        <p className="text-sm text-muted-foreground">{proj.entries} entries</p>
                      </div>
                      <p className="text-xl font-bold text-foreground">{proj.hours.toFixed(1)}h</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No project data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Time Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLog ? 'Edit Time Log' : 'Log Time'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project *</Label>
              <Select 
                value={logForm.project_id} 
                onValueChange={(val) => setLogForm({ ...logForm, project_id: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hours *</Label>
              <Input
                type="number"
                step="0.25"
                value={logForm.hours}
                onChange={(e) => setLogForm({ ...logForm, hours: parseFloat(e.target.value) })}
                placeholder="0.0"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={logForm.date}
                onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Work Type</Label>
              <Select 
                value={logForm.work_type} 
                onValueChange={(val) => setLogForm({ ...logForm, work_type: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="overtime">Overtime</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={logForm.description}
                onChange={(e) => setLogForm({ ...logForm, description: e.target.value })}
                placeholder="What did you work on?"
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={logForm.billable}
                onChange={(e) => setLogForm({ ...logForm, billable: e.target.checked })}
                className="rounded"
              />
              <Label className="font-normal">Billable hours</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogModal(false)}>Cancel</Button>
            <Button onClick={handleSaveLog}>{editingLog ? 'Update' : 'Log Time'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabourTracking;
